import { Tooltip } from '@material-ui/core';
import { AlertIcon } from 'components/Icons';
import SatisfactionSurvey from 'components/Modal/SatisfactionSurvey';
import AssembleiaPautasNews from 'components/__SHARED__/AssembleiaPautas/AssembleiaPautasNews';
import { AssemblyType } from 'helpers/enums/Assembleia';
import { AssembleiaTipo } from 'helpers/enums/AssembleiaTipo';
import { clsx } from 'helpers/utils/HTMLUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import translation from 'locales/translation';
import {
  Col,
  Collapse,
  Row,
  ScreenSize,
  ThreeDotsLoader,
  Title,
} from 'plataforma-fundacao-componentes';
import { Fragment, useCallback, useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { closeModal, openModal } from 'store/modules/ModalActions';
import DocCard from './components/DocCard';
import Context from './Context';
import useStyle from './style';
import styles from './styles.module.scss';
import eventoAPI from 'api/EventoAssociado/evento';
import { toast } from 'react-toastify';
import DownloadTrigger from 'helpers/utils/DownloadTrigger';
import { getContentDispositionFileName } from 'helpers/utils/RequestUtils';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { ErrorApi } from 'api/interfaces';

enum DocCardType {
  Demonstrativo,
  Voucher,
}

interface ProgressState {
  [key: number]: number | null;
}

const deserializeError = (fail: ErrorApi | undefined) =>
  fail
    ? JSON.parse(new TextDecoder().decode(fail as unknown as ArrayBuffer))
    : undefined;

export default function Pautas() {
  const [progress, setProgress] = useState<ProgressState>({
    [DocCardType.Demonstrativo]: null,
    [DocCardType.Voucher]: null,
  });

  const handleProgressUpdate = (
    type: DocCardType,
    newProgress: number | null
  ) => {
    setProgress((prevState) => ({
      ...prevState,
      [type]: newProgress,
    }));
  };

  const resetProgressAfterDelay = (type: DocCardType) =>
    setTimeout(() => {
      handleProgressUpdate(type, null);
    }, 5000);

  const classes = useStyle();
  const {
    usuario,
    currentEvent,
    loadingEvent,
    loadingPautas,
    hasPermissionVerPautas,
    hasPermissionParticipar,
    isConvocado,
  } = useContext(Context);

  const satisfactionSurveyKey = getUniqueKey();
  const dispatch = useDispatch();

  const modalSurvey = useCallback(() => {
    dispatch(
      openModal(SatisfactionSurvey, {
        applicationId: currentEvent && currentEvent.modoEvento,
        eventId: Number(currentEvent && currentEvent.id),
        cpfCnpj: String(usuario?.cpfCnpj),
        modalKey: satisfactionSurveyKey,
        mobileOn: ScreenSize.sm,
        preventEscExit: true,
        preventMaskExit: true,
        onClose: () => closeModal(satisfactionSurveyKey),
      })
    );
  }, [currentEvent, dispatch, satisfactionSurveyKey, usuario?.cpfCnpj]);

  const isPresent = useMemo(() => {
    return (
      currentEvent &&
      currentEvent.contasDisponiveis &&
      currentEvent.contasDisponiveis.filter((acc: { cpfCnpj: string }) => {
        return acc.cpfCnpj !== usuario?.cpfCnpj;
      })
    );
  }, [currentEvent, usuario?.cpfCnpj]);

  const countVotos = useMemo(() => {
    return currentEvent?.pautas?.filter((x: any) => {
      return x.votos.length > 0;
    });
  }, [currentEvent?.pautas]);

  const showSurveyButton = useMemo(() => {
    if (
      currentEvent &&
      currentEvent.flagPesquisa &&
      currentEvent.tipo === AssemblyType.AGN &&
      currentEvent.status === 2 &&
      isPresent &&
      countVotos &&
      countVotos.length
    ) {
      return true;
    }
    return false;
  }, [countVotos, currentEvent, isPresent]);

  const downloadDemonstrativo = async () => {
    if (!currentEvent?.id) {
      toast.error(
        translation.AssembleiaAssociadoRoute.pautas.toast
          .erroDownloadDemonstrativo
      );
      return;
    }

    handleProgressUpdate(DocCardType.Demonstrativo, 0);
    const { data, response, fail } = await eventoAPI.downloadDemostrativo(
      currentEvent?.id
    );

    if (!!data && !!response) {
      const fileName = getContentDispositionFileName(response);
      DownloadTrigger(fileName, data);

      handleProgressUpdate(DocCardType.Demonstrativo, 100);
      resetProgressAfterDelay(DocCardType.Demonstrativo);
    } else {
      const error = deserializeError(fail);

      if (error) {
        ErrorHandler(error, { '#tipoDocumento': 'demonstrativo' });
      } else {
        toast.error(
          translation.AssembleiaAssociadoRoute.pautas.toast
            .erroDownloadDemonstrativo
        );
      }

      handleProgressUpdate(DocCardType.Demonstrativo, null);
    }
  };

  const downloadVoucher = async () => {
    if (!currentEvent?.id || !enableVoucherDownload) {
      toast.error(
        translation.AssembleiaAssociadoRoute.pautas.toast
          .erroDownloadDemonstrativo
      );
      return;
    }

    handleProgressUpdate(DocCardType.Voucher, 0);

    const contas = currentEvent?.contasDisponiveis
      ?.filter(({ presente }) => presente)
      .map(({ numeroConta }) => numeroConta);

    const { data, response, fail } = await eventoAPI.downloadVoucher(
      currentEvent?.id,
      contas ?? []
    );

    if (!!data && !!response) {
      const fileName = getContentDispositionFileName(response);
      DownloadTrigger(fileName, data);
      handleProgressUpdate(DocCardType.Voucher, 100);
      resetProgressAfterDelay(DocCardType.Voucher);
    } else {
      const error = deserializeError(fail);

      if (error) {
        ErrorHandler(error, { '#tipoDocumento': 'voucher' });
      } else {
        toast.error(
          translation.AssembleiaAssociadoRoute.pautas.toast.erroDownloadVoucher
        );
      }

      handleProgressUpdate(DocCardType.Voucher, null);
    }
  };

  const enableVoucherDownload =
    countVotos?.length && countVotos?.length === currentEvent?.pautas?.length;

  return hasPermissionVerPautas ? (
    <Fragment>
      <Row id={'pauta-section'}>
        <Col>
          <Title>{translation.AssembleiaAssociadoRoute.pautas.title}</Title>
        </Col>
        <Collapse animateOpacity opened={loadingEvent || loadingPautas}>
          <Col>
            <div className={styles.bg_pauta}>
              <span className={styles.status_pautas_icon}>
                <ThreeDotsLoader size={1} />
              </span>
              <p className={'nunito'}>
                {translation.AssembleiaAssociadoRoute.pautas.loading}
              </p>
            </div>
          </Col>
        </Collapse>
        <Col noPaddingTop>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
            }}
          >
            {(currentEvent!.pautas || []).length > 0 && (
              <div id={'pautas'} className={classes.pautasId}>
                <AssembleiaPautasNews
                  idEvento={currentEvent?.id!}
                  bloqAlteraVoto={currentEvent?.bloqAlteraVoto}
                  loadingPautas={loadingPautas}
                  pautas={currentEvent?.pautas!}
                  canParticipate={
                    currentEvent?.podeVotar ||
                    (hasPermissionParticipar(
                      currentEvent!.contasDisponiveis || []
                    ) &&
                      currentEvent?.tipo !== AssembleiaTipo.Nucleo &&
                      currentEvent?.tipo !== AssembleiaTipo.Reuniao &&
                      currentEvent?.userCanVote) ||
                    (!currentEvent?.flagMenorSemEmancipado &&
                      !currentEvent?.flagPresencaNaoOnline)
                  }
                  canSeeResults={isConvocado(
                    currentEvent!.contasDisponiveis || []
                  )}
                />
              </div>
            )}
            {!loadingEvent &&
              !loadingPautas &&
              !currentEvent?.pautas?.length && (
                <div className={styles.bg_pauta}>
                  <span
                    className={clsx([
                      styles.status_pautas_icon,
                      styles.status_pauta_icon_warning,
                    ])}
                  >
                    <AlertIcon />
                  </span>
                  <p className={'nunito'}>
                    {translation.AssembleiaAssociadoRoute.pautas.notFound}
                  </p>
                </div>
              )}
          </div>
        </Col>
      </Row>
      {currentEvent && currentEvent.flagPesquisa ? (
        <Row style={{ marginBottom: '40px' }}>
          <Col>
            <Title> {translation.AssembleiaAssociadoRoute.pesquisa}</Title>
          </Col>
          <Col noPaddingTop>
            <Tooltip
              title={'Estará habilitado após responder uma pauta.'}
              placement='top'
              arrow
              disableHoverListener={showSurveyButton}
            >
              <button
                onClick={modalSurvey}
                className={styles.button_survey}
                disabled={!showSurveyButton}
                aria-disabled={!showSurveyButton}
                tabIndex={!showSurveyButton ? -1 : 0}
              >
                <span>{translation.AssembleiaAssociadoRoute.participate}</span>
              </button>
            </Tooltip>
          </Col>
        </Row>
      ) : undefined}
      {currentEvent?.flagMostrarAreaCoop && (
        <Row style={{ marginBottom: '40px' }}>
          <Col>
            <Title> {translation.AssembleiaAssociadoRoute.coopArea}</Title>
          </Col>
          <Col noPaddingTop>
            <p>
              Baixe os documentos da sua Cooperativa -
              <b> {currentEvent?.cooperativa}</b>
            </p>
          </Col>
          <Row>
            <Col cols={[12, 12, 6, 4, 4]}>
              <DocCard
                badge='Demonstrativo'
                description='Baixe o demonstrativo de sua cooperativa'
                onClick={downloadDemonstrativo}
                loader={progress[DocCardType.Demonstrativo]}
              />
            </Col>
            <Col cols={[12, 12, 6, 4, 4]}>
              <DocCard
                badge='Voucher'
                description='Gere o voucher para retirar seu presente.'
                onClick={downloadVoucher}
                loader={progress[DocCardType.Voucher]}
                disabled={!enableVoucherDownload}
              />
            </Col>
          </Row>
        </Row>
      )}
    </Fragment>
  ) : (
    <Fragment />
  );
}
